import {
  BoxProps,
  Button,
  Grid,
  GridItem,
  Heading,
  HStack,
  Icon,
  IconButton,
  Menu,
  MenuButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Stack,
  Tag,
  Text,
  useColorModeValue as mode,
  useTheme,
  useToast,
} from '@chakra-ui/react';
import { transparentize as t } from '@chakra-ui/theme-tools';
import { isRequestDrawerEnabledForTenant } from '@pelicargo/tenants';
import { DrawerType, RequestStatus } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import {
  getPrettyCommodity,
  getPrettyEmailDrawerType,
  getPrettyPaymentStatus,
  getPrettyRequestStatus,
  getPrettyShipmentStatus,
  paymentStatusOptions,
  prettyDate,
  requestStatusOptions,
  shipmentStatusOptions,
} from '@pelicargo/utils';
import uniqBy from 'lodash/uniqBy';
import { useEffect, useMemo } from 'react';
import {
  HiArrowRight,
  HiChevronDown,
  HiLink,
  HiOutlineArrowRightCircle,
  HiOutlineClipboard,
  HiOutlineCube,
  HiOutlineCurrencyDollar,
  HiOutlineDocumentPlus,
  HiOutlineEnvelope,
  HiOutlineTruck,
} from 'react-icons/hi2';

import { trpc } from '../../config/trpc';
import { useCargos } from '../../hooks/cargo/useCargos';
import { useRequestForm } from '../../hooks/request/useForm';
import { useRelatedRequests } from '../../hooks/request/useRelated';
import { GetRequestOutput } from '../../hooks/request/useRequest';
import { useUpdateRequest } from '../../hooks/request/useUpdate';
import { useDashboard } from '../../hooks/useDashboard';
import { useRequestEmails } from '../../hooks/useRequestEmails';
import { paymentStatusUI, requestStatusUI, shipmentStatusUI } from '../../utils/constants';
import {
  getNextEmailDrawerType,
  getNextPaymentStatus,
  getNextRequestStatus,
  getNextShipmentStatus,
} from '../../utils/status';
import { CopyableText } from '../CopyableText';
import { QuickSelect } from '../QuickSelect';
import { SimpleForm } from '../SimpleForm';
import { SimpleQuickSelect } from '../SimpleQuickSelect';
import { Option } from '../SimpleSelect';
import { WithHotKey } from '../WithHotKey';
import { RequestEmailsMenuList } from './RequestEmailsMenuList';
import { LinkableRequest, RequestLinkItem } from './RequestLinkItem';

type Props = BoxProps & {
  request?: GetRequestOutput;
};

const allowedEveryRequestDrawer = Object.keys(DrawerType).every(isRequestDrawerEnabledForTenant);

export const RequestHeader = ({ request, ...rest }: Props) => {
  const toast = useToast();
  const theme = useTheme();
  const dashboard = useDashboard();
  const [cargoList, isFetchingCargos] = useCargos(request?.id);

  const { mutate: updateRequest } = useUpdateRequest(request?.id);
  const { methods } = useRequestForm(request);
  const { data: matchedFilters, refetch: refetchClassification } = trpc.getRequestClassifications.useQuery({
    id: request?.id,
  });
  const firstFilter = matchedFilters?.[0];

  const { related, disclosure: relatedDisclosure, isLoading: loadingRelated } = useRelatedRequests(request?.id);

  const from = useMemo(() => {
    return request?.origin_airports?.map((node) => node?.id)?.join(', ');
  }, [request?.origin_airports]);

  const to = useMemo(() => {
    return request?.destination_airports?.map((node) => node?.id)?.join(', ');
  }, [request?.destination_airports]);

  const { isLoadingBookingAssertion, handleQuickSelectDrawer } = useRequestEmails({ request });

  const handleUpdateStatus = async ({
    status,
    payment_status,
    shipment_status,
  }: Exclude<Parameters<typeof updateRequest>[0], void>) => {
    try {
      await updateRequest({ status, payment_status, shipment_status });
      toast({ title: 'Successfully updated', status: 'success' });
    } catch (error) {
      methods.setValue('status', request.status);
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 10000,
        isClosable: true,
      });
    }
  };

  const nextRequestStatus = useMemo(() => {
    const nextStatus = getNextRequestStatus(request?.status);
    return {
      value: nextStatus,
      label: getPrettyRequestStatus(nextStatus),
    } as Option;
  }, [request?.status]);

  const nextShipmentStatus = useMemo(() => {
    const nextStatus = getNextShipmentStatus(request?.shipment_status, request as Required<typeof request>); // sad
    return {
      value: nextStatus,
      label: getPrettyShipmentStatus(nextStatus),
    } as Option;
  }, [request]);

  const nextPaymentStatus = useMemo(() => {
    const nextStatus = getNextPaymentStatus(request?.payment_status);
    return {
      value: nextStatus,
      label: getPrettyPaymentStatus(nextStatus),
    } as Option;
  }, [request?.payment_status]);

  const nextEmailDrawer = useMemo(() => {
    const currentStatus = request?.status as RequestStatus;
    const nextDrawerType = getNextEmailDrawerType(currentStatus);

    return {
      value: nextDrawerType,
      label: getPrettyEmailDrawerType(nextDrawerType),
    } as Option;
  }, [request?.status]);

  const renderMatchedFilter = (filter, i) => {
    const color = filter?.meta?.['color'];
    return (
      <Tag key={i} size="md" bg={`${color}.100`} color={`${color}.700`}>
        {filter?.identifier}
      </Tag>
    );
  };

  useEffect(() => {
    refetchClassification();
  }, [request, refetchClassification]);

  const renderRequest = (relatedRequest: LinkableRequest) => {
    const isDuplicate = !!related?.duplicate?.find((d) => d.id === Number(relatedRequest.id));
    const isSimilar = !!related?.similar?.find((s) => s.id === Number(relatedRequest.id));

    return (
      <RequestLinkItem
        key={relatedRequest.id}
        request={relatedRequest}
        targetRequest={request}
        isDuplicate={isDuplicate}
        isSimilar={isSimilar}
      />
    );
  };

  const linkIcon = useMemo(() => {
    const isMainRequest = !!request?.linked_requests?.length;
    const isLinked = !!request?.main_request_id || isMainRequest;

    if (!isLinked) {
      return <HiOutlineDocumentPlus title="View potentially duplicate or similar requests" />;
    }

    if (isMainRequest) {
      return (
        <HiLink color="orange" title={`Main request for ${request?.linked_requests?.length || 0} linked requests`} />
      );
    }

    return <HiLink title={`Duplicate of R${request?.main_request_id}`} />;
  }, [request?.linked_requests?.length, request?.main_request_id]);

  const potentialLinkedRequests = useMemo(() => {
    const duplicate =
      related?.duplicate?.map((d) => ({
        ...d,
        id: String(d.id),
        origins: d.origin_airports.map((o) => o.airport_as_origin_id),
        destinations: d.destination_airports.map((d) => d.airport_as_destination_id),
      })) || [];
    const similar =
      related?.similar?.map((s) => ({
        ...s,
        id: String(s.id),
        origins: s.origin_airports.map((o) => o.airport_as_origin_id),
        destinations: s.destination_airports.map((s) => s.airport_as_destination_id),
      })) || [];

    return uniqBy([...duplicate, ...similar], 'id');
  }, [related?.duplicate, related?.similar]);

  const filterColor = firstFilter?.meta?.['color'];

  const handleCopyRequest = () => {
    // Get all the important data from the request
    const requestToCopy = {
      // Request ID
      id: request.id,

      // Origins and destinations
      origins: request.origin_airports.map((o) => o.id),
      destinations: request.destination_airports.map((d) => d.id),

      cargo_origin: request.cargo_origin,

      // Expected dropoff, commodity, temperature range, cooling, UN #, class, packing group, aircraft, packing instruction, section, battery type
      expected_dropoff: request.dropoff_by,
      commodity: getPrettyCommodity(request.commodity),
      cargo_cooling_range: request.cargo_cooling_range,
      cooling: request.cargo_cooling,
      un_number: request.un_number,
      class: request.class,
      packing_group: request.packing_group,
      aircraft: request.aircraft_types,
      packing_instruction: request.packing_instruction,
      section: request.section,
      battery_type: request.battery_type,

      // Cargo details
      cargo: cargoList.map((c) => ({
        quantity: c.quantity,
        weight: c.weight,
        width: c.width,
        height: c.height,
        length: c.length,
        handling: c.handling,
      })),
    };

    navigator.clipboard.writeText(JSON.stringify(requestToCopy, null, 2));

    toast({
      title: 'Request Copied',
      description: 'Request copied to clipboard',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  return (
    <>
      <SimpleForm onValid={handleUpdateStatus} defaultValues={request} {...methods} showToast={false}>
        <Stack p={4} borderBottomWidth={1} spacing={3} {...rest}>
          <Stack direction="row" align="center" justify="space-between">
            <Popover placement="right-end" closeOnBlur isLazy {...relatedDisclosure}>
              <Stack direction="row" align="center" width="fit-content">
                <CopyableText fontSize="3xl" fontWeight="600" userSelect="all" value={`R${request?.id}`} />
                <PopoverTrigger>
                  <IconButton
                    aria-label="Duplicate or similar requests"
                    icon={linkIcon}
                    variant="ghost"
                    fontSize="2xl"
                    isLoading={loadingRelated}
                  />
                </PopoverTrigger>
              </Stack>
              <PopoverContent>
                <PopoverBody p="0">
                  <Stack overflowY="auto" maxH="sm">
                    <Show if={!potentialLinkedRequests?.length && !loadingRelated}>
                      <Stack p={4} align="center" justify="center">
                        <Text color="gray.500">No related requests</Text>
                      </Stack>
                    </Show>
                    {potentialLinkedRequests?.filter((r) => Number(r.id) !== request.id)?.map(renderRequest)}
                  </Stack>
                </PopoverBody>
              </PopoverContent>
            </Popover>

            <HStack>
              <Show if={!!firstFilter}>
                <Popover trigger="hover">
                  <PopoverTrigger>
                    <Tag size="md" bg={`${filterColor}.100`} color={`${filterColor}.700`}>
                      {firstFilter?.identifier}
                    </Tag>
                  </PopoverTrigger>
                  <PopoverContent>
                    <PopoverArrow />
                    <PopoverBody>
                      <Stack>{matchedFilters?.map(renderMatchedFilter)}</Stack>
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              </Show>
              <Show if={request?.is_llm_generated}>
                <Tag size="md" borderRadius="sm" colorScheme="gray">
                  🤖 AI
                </Tag>
              </Show>
              <Text textStyle="captionSmallMedium">{prettyDate(request?.created_at, { showTime: true })}</Text>
            </HStack>
          </Stack>
          <Stack direction="row" align="center" justify="flex-start" spacing={0} p="0.5">
            <Heading variant="h5" fontWeight="400">
              {from}
            </Heading>
            <Icon as={HiArrowRight} fontSize="18px" mx={1.5} />
            <Heading variant="h5" fontWeight="400" textAlign="right">
              {to}
            </Heading>
          </Stack>

          <Grid templateColumns="11em 1fr" gap={2}>
            <HStack>
              <Icon as={HiOutlineCube} />
              <Tag
                colorScheme={requestStatusUI[request?.status]?.colorScheme}
                variant={requestStatusUI[request?.status].variant}
                size="lg"
                w="full"
                fontSize="sm"
              >
                {getPrettyRequestStatus(request?.status)}
              </Tag>
            </HStack>

            <SimpleQuickSelect
              name="status"
              nextValue={nextRequestStatus}
              options={requestStatusOptions}
              buttonProps={{
                leftIcon: <HiOutlineArrowRightCircle />,
              }}
            />
            <HStack>
              <Icon as={HiOutlineTruck} />
              <Tag
                fontSize="sm"
                colorScheme={shipmentStatusUI[request?.shipment_status]?.colorScheme}
                size="lg"
                w="full"
              >
                {getPrettyShipmentStatus(request?.shipment_status)}
              </Tag>
            </HStack>
            <SimpleQuickSelect
              name="shipment_status"
              nextValue={nextShipmentStatus}
              options={shipmentStatusOptions}
              buttonProps={{
                leftIcon: <HiOutlineArrowRightCircle />,
                isDisabled: !nextShipmentStatus.value,
              }}
            />
            <HStack>
              <Icon as={HiOutlineCurrencyDollar} />
              <Tag fontSize="sm" colorScheme={paymentStatusUI[request?.payment_status]?.colorScheme} size="lg" w="full">
                {getPrettyPaymentStatus(request?.payment_status)}
              </Tag>
            </HStack>
            <SimpleQuickSelect
              name="payment_status"
              nextValue={nextPaymentStatus}
              options={paymentStatusOptions}
              buttonProps={{
                leftIcon: <HiOutlineArrowRightCircle />,
                isDisabled: !nextPaymentStatus.value,
              }}
            />

            <GridItem colSpan={2}>
              <Show if={allowedEveryRequestDrawer}>
                <QuickSelect
                  type="menu"
                  nextValue={nextEmailDrawer}
                  buttonProps={{
                    leftIcon: <HiOutlineEnvelope />,
                    onClick: () => handleQuickSelectDrawer(nextEmailDrawer.value as DrawerType),
                  }}
                  dropdownHotkey="e"
                >
                  <MenuButton
                    as={Button}
                    bg={mode('gray.50', t('gray.100', 0.25)(theme))}
                    color={mode('gray.800', 'gray.200')}
                    borderColor={mode('gray.200', 'gray.500')}
                    borderWidth={1}
                    borderLeftWidth={0}
                    rightIcon={<HiChevronDown />}
                    isLoading={isLoadingBookingAssertion}
                    borderLeftRadius={0}
                    paddingInlineStart={1}
                    flex={1}
                    _active={{
                      bg: mode('gray.50', t('gray.100', 0.25)(theme)),
                      borderColor: mode('gray.200', 'gray.500'),
                    }}
                    maxWidth="38px"
                  />
                  <RequestEmailsMenuList request={request} />
                </QuickSelect>
              </Show>
              <Show if={!allowedEveryRequestDrawer}>
                <Menu>
                  <WithHotKey
                    type="menu"
                    dropdownHotkey="e"
                    w="full"
                    bg={mode('gray.50', t('gray.100', 0.25)(theme))}
                    color={mode('gray.800', 'gray.200')}
                    borderColor={mode('gray.200', 'gray.500')}
                    leftIcon={<HiOutlineEnvelope />}
                    textAlign="left"
                    borderWidth={1}
                    rightIcon={<HiChevronDown />}
                    isLoading={isLoadingBookingAssertion}
                    flex={1}
                    _active={{
                      bg: mode('gray.50', t('gray.100', 0.25)(theme)),
                      borderColor: mode('gray.200', 'gray.500'),
                    }}
                  >
                    Send Update
                  </WithHotKey>
                  <RequestEmailsMenuList request={request} />
                </Menu>
              </Show>
            </GridItem>
          </Grid>
          <Show if={dashboard.isDevMode}>
            <Button
              onClick={handleCopyRequest}
              leftIcon={<HiOutlineClipboard />}
              variant="ghost"
              isLoading={isFetchingCargos}
            >
              Copy Request Details
            </Button>
          </Show>
        </Stack>
      </SimpleForm>
    </>
  );
};
